import { useState } from 'react';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { 
  Users, 
  UserCheck, 
  Mail, 
  Phone, 
  Calendar,
  FileText,
  DollarSign,
  Key,
  Search,
  Plus,
  Edit,
  Eye,
  Clock,
  AlertCircle,
  CheckCircle,
  Building,
  Home,
  ArrowRight,
  ArrowLeft
} from 'lucide-react';
import { useTumTenants } from '@/hooks/useTumTenants';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { TumTenantForm } from '@/components/tum/TumTenantForm';
import { format } from 'date-fns';

export default function TumTenants() {
  const { currentWorkspace } = useWorkspace();
  const { tenants, isLoading: tenantsLoading } = useTumTenants();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedTenant, setSelectedTenant] = useState<string | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  // Fetch leases for tenants (if leases table exists)
  const { data: leasesData } = useQuery({
    queryKey: ['tum-leases', currentWorkspace?.id, selectedTenant],
    queryFn: async () => {
      if (!currentWorkspace?.id) return [];
      // Check if tum_leases table exists, if not return empty array
      try {
        let query = supabase
          .from('tum_leases')
          .select(`
            *,
            tenant:tum_tenants(id, display_name),
            unit:tum_units(id, unit_number, property:tum_properties(id, property_name))
          `)
          .eq('workspace_id', currentWorkspace.id)
          .order('lease_start_date', { ascending: false });
        
        if (selectedTenant) {
          query = query.eq('tenant_id', selectedTenant);
        }
        
        const { data, error } = await query;
        if (error) {
          // Table might not exist yet, return empty array
          if (error.code === '42P01') return [];
          throw error;
        }
        return data || [];
      } catch {
        return [];
      }
    },
    enabled: !!currentWorkspace?.id,
  });

  // Fetch bills for tenants
  const { data: billsData } = useQuery({
    queryKey: ['tum-tenant-bills', currentWorkspace?.id, selectedTenant],
    queryFn: async () => {
      if (!currentWorkspace?.id) return [];
      let query = supabase
        .from('bills')
        .select('*, tenant:tum_tenants(id, display_name)')
        .eq('workspace_id', currentWorkspace.id)
        .order('bill_date', { ascending: false })
        .limit(100);
      
      // Note: Bills are linked to tenants through bill allocations
      // For now, we'll show all bills - filtering by tenant would require joining with tum_bill_allocations
      
      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    },
    enabled: !!currentWorkspace?.id,
  });

  // Fetch tenant activity/history
  const { data: activityData } = useQuery({
    queryKey: ['tum-tenant-activity', currentWorkspace?.id, selectedTenant],
    queryFn: async () => {
      if (!currentWorkspace?.id || !selectedTenant) return [];
      // This would query an activity log table if it exists
      // For now, return empty array
      return [];
    },
    enabled: !!currentWorkspace?.id && !!selectedTenant,
  });

  const filteredTenants = tenants.filter(tenant => {
    const matchesSearch = 
      tenant.display_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tenant.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tenant.phone?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tenant.tenant_code?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || tenant.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const selectedTenantData = selectedTenant 
    ? tenants.find(t => t.id === selectedTenant)
    : null;

  // Calculate stats
  const totalTenants = tenants.length;
  const activeTenants = tenants.filter(t => t.status === 'active').length;
  const prospectTenants = tenants.filter(t => t.status === 'prospect').length;
  const noticePeriodTenants = tenants.filter(t => t.status === 'notice_period').length;
  const portalEnabled = tenants.filter(t => t.portal_enabled).length;

  // Get tenant-specific data
  const tenantLeases = selectedTenant
    ? (leasesData || []).filter((l: any) => l.tenant_id === selectedTenant)
    : [];
  const tenantBills = selectedTenant
    ? (billsData || []).slice(0, 20) // Show recent bills
    : [];

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (date: string | Date) => {
    try {
      return format(new Date(date), 'MMM dd, yyyy');
    } catch {
      return 'N/A';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'default';
      case 'prospect': return 'secondary';
      case 'notice_period': return 'outline';
      case 'former': return 'secondary';
      default: return 'secondary';
    }
  };

  if (tenantsLoading) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center py-12">
          <Clock className="h-8 w-8 mx-auto mb-2 animate-spin text-muted-foreground" />
          <p className="text-muted-foreground">Loading tenants...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <PageHeader 
        title="Tenants" 
        description="Manage tenants, leases, move-ins, and move-outs"
      />
      
      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Tenants</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalTenants}</div>
            <p className="text-xs text-muted-foreground">
              {activeTenants} active
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Tenants</CardTitle>
            <UserCheck className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{activeTenants}</div>
            <p className="text-xs text-muted-foreground">
              Currently active
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Prospects</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{prospectTenants}</div>
            <p className="text-xs text-muted-foreground">
              In notice period: {noticePeriodTenants}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Portal Access</CardTitle>
            <Key className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{portalEnabled}</div>
            <p className="text-xs text-muted-foreground">
              Enabled portal access
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Actions */}
      <div className="flex justify-between items-center gap-4">
        <div className="flex gap-4 flex-1">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search tenants by name, email, phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border rounded-md text-sm"
            aria-label="Filter by status"
          >
            <option value="all">All Status</option>
            <option value="prospect">Prospect</option>
            <option value="active">Active</option>
            <option value="notice_period">Notice Period</option>
            <option value="former">Former</option>
          </select>
        </div>

        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Add Tenant
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add New Tenant</DialogTitle>
            </DialogHeader>
            <TumTenantForm onSuccess={() => setIsCreateDialogOpen(false)} />
          </DialogContent>
        </Dialog>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Tenants List */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle>Tenants</CardTitle>
              <CardDescription>
                {filteredTenants.length} tenant{filteredTenants.length !== 1 ? 's' : ''} found
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <div className="max-h-[600px] overflow-y-auto">
                {filteredTenants.length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground">
                    <Users className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>No tenants found</p>
                    <p className="text-sm mt-1">Create your first tenant to get started</p>
                  </div>
                ) : (
                  <div className="divide-y">
                    {filteredTenants.map(tenant => (
                      <div
                        key={tenant.id}
                        onClick={() => {
                          setSelectedTenant(tenant.id);
                          setActiveTab('overview');
                        }}
                        className={`p-4 cursor-pointer hover:bg-muted/50 transition-colors ${
                          selectedTenant === tenant.id ? 'bg-muted' : ''
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold">{tenant.display_name}</h3>
                              <Badge variant={getStatusColor(tenant.status)}>
                                {tenant.status.replace('_', ' ')}
                              </Badge>
                            </div>
                            {tenant.tenant_code && (
                              <p className="text-xs text-muted-foreground mb-1">
                                Code: {tenant.tenant_code}
                              </p>
                            )}
                            {tenant.email && (
                              <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                                <Mail className="h-3 w-3" />
                                <span>{tenant.email}</span>
                              </div>
                            )}
                            {tenant.phone && (
                              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                <Phone className="h-3 w-3" />
                                <span>{tenant.phone}</span>
                              </div>
                            )}
                            {tenant.portal_enabled && (
                              <Badge variant="outline" className="mt-2 text-xs">
                                <Key className="h-3 w-3 mr-1" />
                                Portal Enabled
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
          </div>
            </CardContent>
          </Card>
        </div>

        {/* Tenant Details */}
        <div className="lg:col-span-2">
          {selectedTenantData ? (
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-2xl">{selectedTenantData.display_name}</CardTitle>
                    <CardDescription className="mt-1">
                      {selectedTenantData.tenant_code && `Code: ${selectedTenantData.tenant_code} • `}
                      {selectedTenantData.tenant_type && `${selectedTenantData.tenant_type} • `}
                      {selectedTenantData.portal_enabled && 'Portal Enabled'}
                    </CardDescription>
                  </div>
                  <Badge variant={getStatusColor(selectedTenantData.status)}>
                    {selectedTenantData.status.replace('_', ' ')}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  <TabsList className="grid w-full grid-cols-5">
                    <TabsTrigger value="overview">Overview</TabsTrigger>
                    <TabsTrigger value="leases">Leases</TabsTrigger>
                    <TabsTrigger value="bills">Bills</TabsTrigger>
                    <TabsTrigger value="portal">Portal</TabsTrigger>
                    <TabsTrigger value="history">History</TabsTrigger>
                  </TabsList>

                  <TabsContent value="overview" className="space-y-4 mt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Contact Information</label>
                        <div className="text-sm mt-1 space-y-1">
                          {selectedTenantData.email && (
                            <div className="flex items-center gap-2">
                              <Mail className="h-3 w-3 text-muted-foreground" />
                              <span>{selectedTenantData.email}</span>
                            </div>
                          )}
                          {selectedTenantData.phone && (
                            <div className="flex items-center gap-2">
                              <Phone className="h-3 w-3 text-muted-foreground" />
                              <span>{selectedTenantData.phone}</span>
                            </div>
                          )}
                          {selectedTenantData.alternate_phone && (
                            <div className="flex items-center gap-2">
                              <Phone className="h-3 w-3 text-muted-foreground" />
                              <span className="text-muted-foreground">Alt: {selectedTenantData.alternate_phone}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Tenant Details</label>
                        <div className="text-sm mt-1 space-y-1">
                          <p>Type: <span className="capitalize">{selectedTenantData.tenant_type || 'N/A'}</span></p>
                          {selectedTenantData.company_name && (
                            <p>Company: {selectedTenantData.company_name}</p>
                          )}
                          {selectedTenantData.company_registration && (
                            <p className="text-muted-foreground">Reg: {selectedTenantData.company_registration}</p>
                          )}
                        </div>
                      </div>
                      {selectedTenantData.emergency_contact_name && (
                        <div>
                          <label className="text-sm font-medium text-muted-foreground">Emergency Contact</label>
                          <div className="text-sm mt-1">
                            <p>{selectedTenantData.emergency_contact_name}</p>
                            {selectedTenantData.emergency_contact_phone && (
                              <p className="text-muted-foreground">{selectedTenantData.emergency_contact_phone}</p>
                            )}
                            {selectedTenantData.emergency_contact_relation && (
                              <p className="text-muted-foreground">{selectedTenantData.emergency_contact_relation}</p>
                            )}
                          </div>
                        </div>
                      )}
                      {selectedTenantData.notes && (
                        <div>
                          <label className="text-sm font-medium text-muted-foreground">Notes</label>
                          <p className="text-sm mt-1">{selectedTenantData.notes}</p>
                        </div>
                      )}
                    </div>

                    {/* Quick Stats */}
                    <div className="grid grid-cols-3 gap-4 pt-4 border-t">
                      <div>
                        <p className="text-sm text-muted-foreground">Active Leases</p>
                        <p className="text-2xl font-bold">{tenantLeases.filter((l: any) => l.status === 'active').length}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Total Bills</p>
                        <p className="text-2xl font-bold">{tenantBills.length}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Portal Status</p>
                        <p className="text-2xl font-bold">
                          {selectedTenantData.portal_enabled ? (
                            <CheckCircle className="h-6 w-6 text-green-500" />
                          ) : (
                            <AlertCircle className="h-6 w-6 text-muted-foreground" />
                          )}
                        </p>
                      </div>
                    </div>
                  </TabsContent>

                  <TabsContent value="leases" className="mt-4">
                    {tenantLeases.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <Home className="h-12 w-12 mx-auto mb-2 opacity-50" />
                        <p>No leases found for this tenant</p>
                        <Button className="mt-4" variant="outline">
                          <Plus className="h-4 w-4 mr-2" />
                          Create Lease
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {tenantLeases.map((lease: any) => (
                          <Card key={lease.id}>
                            <CardHeader>
                              <div className="flex items-start justify-between">
                                <div>
                                  <CardTitle className="text-lg">
                                    {lease.unit?.unit_number || 'N/A'} - {lease.unit?.property?.property_name || 'N/A'}
                                  </CardTitle>
                                  <CardDescription>
                                    {lease.lease_start_date && formatDate(lease.lease_start_date)} - {lease.lease_end_date && formatDate(lease.lease_end_date)}
                                  </CardDescription>
                                </div>
                                <Badge variant={lease.status === 'active' ? 'default' : 'secondary'}>
                                  {lease.status}
                                </Badge>
                              </div>
                            </CardHeader>
                            <CardContent>
                              <div className="grid grid-cols-2 gap-4 text-sm">
                                <div>
                                  <p className="text-muted-foreground">Monthly Rent</p>
                                  <p className="font-semibold">{lease.monthly_rent ? formatCurrency(lease.monthly_rent) : 'N/A'}</p>
                                </div>
                                <div>
                                  <p className="text-muted-foreground">Security Deposit</p>
                                  <p className="font-semibold">{lease.security_deposit ? formatCurrency(lease.security_deposit) : 'N/A'}</p>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="bills" className="mt-4">
                    {tenantBills.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
                        <p>No bills found for this tenant</p>
                      </div>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Bill Date</TableHead>
                            <TableHead>Account</TableHead>
                            <TableHead>Commodity</TableHead>
                            <TableHead>Amount</TableHead>
                            <TableHead>Status</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {tenantBills.map((bill: any) => (
                            <TableRow key={bill.id}>
                              <TableCell>{bill.bill_date ? formatDate(bill.bill_date) : 'N/A'}</TableCell>
                              <TableCell>{bill.account_number || 'N/A'}</TableCell>
                              <TableCell className="capitalize">{bill.commodity || 'N/A'}</TableCell>
                              <TableCell>{formatCurrency(parseFloat(bill.amount?.toString() || '0'))}</TableCell>
                              <TableCell>
                                <Badge variant={bill.status === 'paid' ? 'default' : 'secondary'}>
                                  {bill.status || 'pending'}
                                </Badge>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </TabsContent>

                  <TabsContent value="portal" className="mt-4">
                    <div className="space-y-4">
                      <Card>
                        <CardHeader>
                          <CardTitle>Portal Access</CardTitle>
                          <CardDescription>
                            Manage tenant portal access and settings
                          </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium">Portal Status</p>
                              <p className="text-sm text-muted-foreground">
                                {selectedTenantData.portal_enabled 
                                  ? 'Tenant has access to the portal' 
                                  : 'Portal access is disabled'}
                              </p>
                            </div>
                            <Badge variant={selectedTenantData.portal_enabled ? 'default' : 'secondary'}>
                              {selectedTenantData.portal_enabled ? 'Enabled' : 'Disabled'}
                            </Badge>
                          </div>
                          {selectedTenantData.portal_user_id && (
                            <div>
                              <p className="text-sm font-medium">Portal User ID</p>
                              <p className="text-sm text-muted-foreground">{selectedTenantData.portal_user_id}</p>
                            </div>
                          )}
                          <Button variant="outline">
                            {selectedTenantData.portal_enabled ? 'Disable Portal' : 'Enable Portal'}
                          </Button>
                        </CardContent>
                      </Card>
                    </div>
                  </TabsContent>

                  <TabsContent value="history" className="mt-4">
                    <div className="text-center py-8 text-muted-foreground">
                      <Clock className="h-12 w-12 mx-auto mb-2 opacity-50" />
                      <p>Activity history will be displayed here</p>
                      <p className="text-sm mt-1">Move-in/move-out events, lease changes, and other activities</p>
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <Users className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                <h3 className="text-lg font-semibold mb-2">Select a Tenant</h3>
                <p className="text-muted-foreground">
                  Choose a tenant from the list to view details, leases, bills, and portal access.
                </p>
              </CardContent>
      </Card>
          )}
        </div>
      </div>
    </div>
  );
}
